//
//  components/pwa/PWAInstallProvider.tsx
//  TownLIT
//
//  Created by Hossein Sakkaki on 2026-08-19.
//  Last Update by Hossein Sakkaki on 2026-08-19.
//

"use client";

import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

export type PWAInstallOutcome =
  | "accepted"
  | "dismissed"
  | "unavailable";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
};

export type PWAInstallContextValue = {
  canInstall: boolean;
  alreadyInstalled: boolean;
  install: () => Promise<PWAInstallOutcome>;
};

export const PWAInstallContext =
  createContext<PWAInstallContextValue | null>(null);

function isRunningStandalone(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone ===
      true
  );
}

export default function PWAInstallProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [promptEvent, setPromptEvent] =
    useState<BeforeInstallPromptEvent | null>(null);

  const [alreadyInstalled, setAlreadyInstalled] = useState(false);

  useEffect(() => {
    setAlreadyInstalled(isRunningStandalone());

    const standaloneQuery = window.matchMedia(
      "(display-mode: standalone)",
    );

    const handleStandaloneChange = () => {
      setAlreadyInstalled(isRunningStandalone());
    };

    const handleBeforeInstallPrompt = (event: Event) => {
      const installEvent = event as BeforeInstallPromptEvent;

      installEvent.preventDefault();
      setPromptEvent(installEvent);
    };

    const handleAppInstalled = () => {
      setPromptEvent(null);
      setAlreadyInstalled(true);
    };

    standaloneQuery.addEventListener(
      "change",
      handleStandaloneChange,
    );

    window.addEventListener(
      "beforeinstallprompt",
      handleBeforeInstallPrompt,
    );

    window.addEventListener(
      "appinstalled",
      handleAppInstalled,
    );

    return () => {
      standaloneQuery.removeEventListener(
        "change",
        handleStandaloneChange,
      );

      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );

      window.removeEventListener(
        "appinstalled",
        handleAppInstalled,
      );
    };
  }, []);

  const install = useCallback(async (): Promise<PWAInstallOutcome> => {
    if (!promptEvent || alreadyInstalled) {
      return "unavailable";
    }

    const activePrompt = promptEvent;

    // A browser install prompt can only be consumed once.
    setPromptEvent(null);

    try {
      await activePrompt.prompt();

      const result = await activePrompt.userChoice;

      return result.outcome;
    } catch {
      return "unavailable";
    }
  }, [promptEvent, alreadyInstalled]);

  const value = useMemo<PWAInstallContextValue>(
    () => ({
      canInstall: Boolean(promptEvent) && !alreadyInstalled,
      alreadyInstalled,
      install,
    }),
    [promptEvent, alreadyInstalled, install],
  );

  return (
    <PWAInstallContext.Provider value={value}>
      {children}
    </PWAInstallContext.Provider>
  );
}